import { useCallback, useContext, useMemo } from 'react';
import { createSearchParams, useNavigate, useSearchParams } from 'react-router-dom';
import { OrganisationContext } from '../../context';
import { OrganisationTag, OrganisationTagType } from '../../services';
import { isAdminLayout, layoutPath, optArrItem } from '../utils';

export const useSearchByTags = () => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const qs = searchParams.get('qs') ?? '';

  const tagsSearchParams = searchParams.get('tags') ?? '';

  const searchPagePath = useMemo(() => layoutPath('/search'), []);

  const tagsToSearchBy = useContext(OrganisationContext).organisation.tags.filter(({ type }) =>
    [
      ...optArrItem(isAdminLayout(), OrganisationTagType.INTERNAL),
      OrganisationTagType.EXTERNAL,
    ].includes(type)
  );

  const isTagSelected = useCallback(
    ({ id }: OrganisationTag) => {
      return tagsSearchParams.split('_').includes(`${id}`);
    },
    [tagsSearchParams]
  );

  const getTagIdsToSearchByType = useCallback(
    (organisationTagType: OrganisationTagType) => {
      return tagsToSearchBy
        .filter(
          ({ id, type }) =>
            tagsSearchParams.split('_').includes(`${id}`) && type === organisationTagType
        )
        .map(({ id }) => Number(id));
    },
    [tagsSearchParams, tagsToSearchBy]
  );

  const tagIdsToSearchBy = useMemo(() => {
    if (!tagsSearchParams) {
      return;
    }

    return {
      organisationTags: {
        internalTagsId: getTagIdsToSearchByType(OrganisationTagType.INTERNAL),
        externalTagsId: getTagIdsToSearchByType(OrganisationTagType.EXTERNAL),
      },
    };
  }, [getTagIdsToSearchByType, tagsSearchParams]);

  const searchByTagIdRemoval = useCallback(
    (tagId: number) => {
      navigate({
        pathname: searchPagePath,
        search: `?${createSearchParams({
          qs,
          tags: tagsSearchParams
            ? `${tagsSearchParams
                .split('_')
                .filter((id) => id !== `${tagId}`)
                .join('_')}`
            : '',
        })}`,
      });
    },
    [navigate, qs, searchPagePath, tagsSearchParams]
  );

  return {
    tagsSearchParams,
    tagsToSearchBy,
    tagIdsToSearchBy,
    isTagSelected,
    searchByTagIdRemoval,
  };
};
